import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Upload, FileText, Image, Crown, Users, Star, ArrowRight,
  Target, Heart, Globe, Sparkles, Eye, Shield
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import UniversalHeartToggle from './UniversalHeartToggle';

const GuildCreationOnboarding = ({ player, onComplete }) => {
  const [guildName, setGuildName] = useState('');
  const [guildSubtitle, setGuildSubtitle] = useState('');
  const [guildManifesto, setGuildManifesto] = useState('');
  const [guildImage, setGuildImage] = useState(null);
  const [uploadedManifesto, setUploadedManifesto] = useState(null);
  const [focusTags, setFocusTags] = useState([]);
  const [spiralPhase, setSpiralPhase] = useState(0);
  
  const imageFileRef = useRef(null);
  const manifestoFileRef = useRef(null);

  // Grant spiral animation
  React.useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  const availableTags = [
    'regenerative-agriculture', 'consciousness-expansion', 'family-projects', 
    'business-innovation', 'community-building', 'spiritual-practice',
    'environmental-healing', 'creative-collaboration', 'wisdom-sharing',
    'mycology', 'permaculture', 'trust-economics', 'gift-economy'
  ];

  const toggleTag = (tag) => {
    setFocusTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setGuildImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleManifestoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedManifesto(event.target.result);
        setGuildManifesto(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const handleComplete = () => {
    if (!guildName.trim() || !guildSubtitle.trim() || !guildManifesto.trim()) {
      alert('Please complete guild name, subtitle, and manifesto');
      return;
    }

    const guildData = {
      name: guildName,
      subtitle: guildSubtitle,
      manifesto: guildManifesto,
      image: guildImage,
      focus_tags: focusTags,
      founder_id: player.user?.id,
      founder_name: player.name,
      created_at: new Date().toISOString(),
      semi_independent: true,
      integration_enabled: true
    };

    // Store guild creation data
    localStorage.setItem('guild_creation_data', JSON.stringify(guildData));

    // Enhanced player with guild founder status
    const enhancedPlayer = {
      ...player,
      guild: guildData,
      consciousness_level: 'guild_founder',
      access_level: 'guild_facilitator',
      roles: [...(player.user?.roles || []), 'guild_founder', 'guild_facilitator']
    };

    console.log('🏰 GUILD CREATION COMPLETE:', guildData);
    onComplete(enhancedPlayer);
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_mairy-chat/artifacts/r47m1ygo_DA63FE00-9235-4124-AE03-E55E266118BE.webp)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(12) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) saturate(1.6) brightness(1.3)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/20 to-black/60"></div>

      <div className="relative z-10 min-h-screen p-6 flex items-center justify-center">
        <div className="w-full max-w-5xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.8)'
              }}
            >
              🏰 Create Your Guild
            </h1>
            <p 
              className="text-yellow-200 text-xl"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              Design your consciousness community within the Secret History
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Left Column: Guild Basics */}
            <div className="space-y-6">
              
              {/* Guild Identity */}
              <Card className="bg-black/60 backdrop-blur-xl border border-yellow-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-white text-lg font-bold mb-4">Guild Identity</h3>
                  
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={guildName}
                      onChange={(e) => setGuildName(e.target.value)}
                      placeholder="Guild Name (e.g., Mycology Collective, Regenerative Innovators)"
                      className="w-full p-4 bg-yellow-800/50 border border-yellow-400/50 rounded-xl text-white placeholder-yellow-200"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    />
                    
                    <input
                      type="text"
                      value={guildSubtitle}
                      onChange={(e) => setGuildSubtitle(e.target.value)}
                      placeholder="Guild Mission (brief description of your focus)"
                      className="w-full p-4 bg-yellow-800/50 border border-yellow-400/50 rounded-xl text-white placeholder-yellow-200"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    />
                    
                    {/* Guild Image Upload */}
                    <div className="text-center">
                      <Button
                        onClick={() => imageFileRef.current?.click()}
                        variant="outline"
                        className="text-yellow-300 border-yellow-400/50 hover:bg-yellow-500/20"
                      >
                        <Image className="w-4 h-4 mr-2" />
                        Upload Guild Image
                      </Button>
                      <input
                        ref={imageFileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      {guildImage && (
                        <div className="mt-4">
                          <img 
                            src={guildImage} 
                            alt="Guild preview"
                            className="w-24 h-24 rounded-xl mx-auto border-2 border-yellow-400/50"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Focus Tags */}
              <Card className="bg-black/60 backdrop-blur-xl border border-purple-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-white text-lg font-bold mb-4">Guild Focus Areas</h3>
                  <p className="text-purple-100 text-sm mb-4">
                    Select areas your guild will focus on (helps with matching and community building)
                  </p>
                  
                  <div className="grid grid-cols-2 gap-2">
                    {availableTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`p-2 rounded-lg border text-xs transition-all ${
                          focusTags.includes(tag)
                            ? 'bg-purple-500/30 border-purple-400 text-purple-200'
                            : 'bg-black/30 border-purple-400/30 text-purple-300 hover:bg-purple-500/20'
                        }`}
                      >
                        {tag.replace('-', ' ')}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column: Guild Manifesto */}
            <div className="space-y-6">
              
              {/* Guild Manifesto */}
              <Card className="bg-black/60 backdrop-blur-xl border border-cyan-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-white text-lg font-bold mb-4">Guild Manifesto</h3>
                  <p className="text-cyan-100 text-sm mb-4">
                    Write your guild's shared vision and values that will guide M.ai.ry's interactions with your members
                  </p>
                  
                  <textarea
                    value={guildManifesto}
                    onChange={(e) => setGuildManifesto(e.target.value)}
                    placeholder="Our guild exists to... We believe in... Our approach is..."
                    className="w-full p-4 bg-cyan-800/30 border border-cyan-400/50 rounded-xl text-white placeholder-cyan-200 resize-none"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    rows={8}
                  />
                  
                  {/* Manifesto Upload */}
                  <div className="mt-4 text-center">
                    <Button
                      onClick={() => manifestoFileRef.current?.click()}
                      variant="outline"
                      className="text-cyan-300 border-cyan-400/50"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Upload Manifesto File
                    </Button>
                    <input
                      ref={manifestoFileRef}
                      type="file"
                      accept=".txt,text/plain"
                      onChange={handleManifestoUpload}
                      className="hidden"
                    />
                    {uploadedManifesto && (
                      <div className="mt-2">
                        <Badge className="bg-cyan-600 text-white">
                          📄 Manifesto file uploaded
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Guild Benefits */}
              <Card className="bg-black/60 backdrop-blur-xl border border-green-400/30 shadow-2xl">
                <CardContent className="p-6">
                  <h3 className="text-green-200 text-lg font-bold mb-4">Your Guild Benefits</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center space-x-3">
                      <Crown className="w-5 h-5 text-green-400" />
                      <span className="text-green-100">Semi-independent play with custom M.ai.ry responses</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-green-400" />
                      <span className="text-green-100">Invite system based on your manifesto values</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Target className="w-5 h-5 text-green-400" />
                      <span className="text-green-100">Custom project salon themes and focus areas</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Globe className="w-5 h-5 text-green-400" />
                      <span className="text-green-100">Option to integrate with broader Secret History community</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Complete Guild Creation */}
          <div className="text-center mt-8">
            <Button
              onClick={handleComplete}
              disabled={!guildName.trim() || !guildSubtitle.trim() || !guildManifesto.trim()}
              className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-12 py-6 text-xl font-bold"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              <Crown className="w-6 h-6 mr-3" />
              Establish Your Guild
            </Button>
            
            <p className="text-white/60 text-sm mt-4 italic">
              Your guild will have its own consciousness space within the Secret History
            </p>
          </div>
        </div>
      </div>
      
      <UniversalHeartToggle />
      <PersistentFooter />
    </div>
  );
};

export default GuildCreationOnboarding;